<script setup>
import { computed } from '#imports';

const props = defineProps({
  author: {
    type: Object,
    required: true,
  },
});

const showContactsBlock = computed(
  () => props.author.email || props.author.socials || props.author.pgp
);
</script>

<template>
  <div class="author-header content-container">
    <div class="author-header__column author-header__column--left">
      <div class="author-header__inner">
        <div class="author-header__avatar">
          <BaseAvatar :image="author.image" size="huge" />
        </div>
        <div class="author-header__title-wrap">
          <h1 class="author-header__title">
            {{ author.name }}
          </h1>
          <ul v-if="author.keywords" class="author-header__keywords">
            <li
              v-for="item in author.keywords"
              :key="item"
              class="author-header__keywords-item"
            >
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
      <div v-if="author.content" class="author-header__description">
        <PostContent :content="author.content" />
      </div>
    </div>
    <div v-if="showContactsBlock" class="author-header__column">
      <div class="author-header__contacts-title">Как связаться?</div>
      <div
        v-if="author.contactsDescription"
        class="author-header__contacts-description"
      >
        {{ author.contactsDescription }}
      </div>
      <div class="author-header__contacts">
        <div v-if="author.socials" class="author-header__contacts-item-wrap">
          <AuthorHeaderSocials :socials="author.socials" />
        </div>
        <div v-if="author.email" class="author-header__contacts-item-wrap">
          <span class="author-header__caption">Электронная почта</span>
          <a
            :href="`mailto:${author.email}`"
            class="author-header__contacts-item"
          >
            {{ author.email }}
          </a>
        </div>
      </div>
      <div v-if="author.pgp" class="author-header__contacts-item-wrap">
        <span class="author-header__caption"
          >Публичный PGP-ключ и Fingerprint:</span
        >
        <BaseCopyToClipboard :value="author.pgp.fingerprint">
          <span class="author-header__fingerprint">{{
            author.pgp.fingerprint
          }}</span>
        </BaseCopyToClipboard>
        <br />
        <BaseCopyToClipboard :value="author.pgp.publicKey">
          <span class="author-header__contacts-item">Public Key</span>
        </BaseCopyToClipboard>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.author-header {
  display: grid;
  padding: 32px 20px 80px;
  grid-template-columns: 3fr 1fr;
  font-family: var(--body-font-family);
  font-size: var(--body-font-size);
  line-height: var(--body-line-height);

  @media (--tablet-vp) {
    display: flex;
    flex-direction: column;
    padding: 16px 16px 40px;
  }

  &__column {
    @media (--tablet-vp) {
      max-width: var(--content-max-width);
      padding: 0 46px;
      margin-top: 24px;
    }

    @media (--small-vp) {
      padding: 0;
    }

    &--left {
      margin-right: 20px;
      margin-top: 0;
    }
  }

  &__inner {
    display: flex;
    gap: 32px;
  }

  &__avatar {
    min-width: 128px;
  }

  &__title-wrap {
    max-width: 100%;
  }

  &__title {
    font-family: var(--title-font-family);
    font-size: var(--h1-font-size);
    line-height: var(--h1-line-height);
    word-break: break-word;
  }

  &__keywords {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;

    @media (--tablet-vp) {
      gap: 8px;
    }
  }

  &__keywords-item {
    font-size: 16px;
    line-height: 24px;
    padding: 3px 12px;
    border: 1px solid #f686ef;
    border-radius: var(--border-radius);
  }

  &__description {
    margin-top: 32px;
    padding-left: 24px;

    @media (--tablet-vp) {
      padding-left: 0;
    }
  }

  &__contacts-title {
    margin-bottom: 16px;
    font-family: var(--title-font-family);
    font-size: var(--h3-font-size);
    line-height: var(--h3-line-height);
    font-weight: 700;
    max-width: 292px;

    @media (--tablet-vp) {
      max-width: none;
    }
  }

  &__contacts-description {
    margin-bottom: 20px;
  }

  &__contacts-item-wrap {
    margin-bottom: 20px;
  }

  &__caption {
    display: block;
    font-size: 14px;
    line-height: 22px;
    color: rgba(var(--black-color--rgb), 60%);
  }

  &__contacts-item {
    display: inline-block;
    padding: 2px 4px;
    font-weight: 500;
    font-size: 18px;
    line-height: 26px;
    color: var(--accent-color);
    background-color: #f2e6f1;
    border-radius: 4px;
    transform: translateX(-4px);
    transition: var(--default-transition) opacity;

    &:hover {
      opacity: 0.6;
    }
  }

  &__fingerprint {
    display: block;
    max-width: 256px;
    margin-bottom: 8px;
    font-size: 18px;
    line-height: 26px;
    font-weight: 500;
  }

  @media (--tablet-vp) {
    &__contacts {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 60px;
    }
  }

  @media (--small-vp) {
    flex-direction: column;
    gap: 24px;
    padding: 16px 12px 40px;

    &__inner {
      flex-direction: row-reverse;
      gap: 12px;
    }

    &__title {
      font-size: 24px;
      line-height: 30px;
    }

    &__column {
      &--left {
        min-width: auto;
      }
    }

    &__avatar {
      min-width: 100px;
      margin-left: auto;
    }

    &__keywords {
      gap: 4px;
      margin-top: 8px;
    }

    &__keywords-item {
      font-size: 12px;
      line-height: 20px;
      padding: 2px 8px;
      border-radius: 12px;
    }

    &__description {
      margin-top: 24px;
      padding-left: 0;
      font-size: 16px;
      line-height: 22px;
    }

    &__contacts-title {
      font-size: 20px;
      line-height: 30px;
      margin-bottom: 8px;
    }

    &__contacts-description {
      font-size: 16px;
      line-height: 22px;
      margin-bottom: 16px;
    }

    &__contacts-item-wrap {
      margin-bottom: 16px;
    }

    &__contacts-item {
      font-size: 14px;
      line-height: 20px;
      padding: 0 3px;
    }

    &__caption {
      font-size: 12px;
      line-height: 15px;
    }

    &__fingerprint {
      max-width: none;
      margin-bottom: 0;
      font-size: 14px;
      line-height: 20px;
    }
  }
}
</style>
